import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { isOrthographicVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import {
  SensorFrame2D,
  SensorRendererState,
} from '../../../cross-team-shared/state/sensor-renderer-state';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';

@Component({
  selector: 'h3-image-renderer',
  templateUrl: './image-renderer.component.html',
  styleUrls: ['./image-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ImageRendererComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input() isInsideZoomPanel = false;

  sensorRendererState$!: Observable<SensorRendererState<SensorFrame2D>>;
  src$!: Observable<string | undefined>;
  filter$!: Observable<SafeStyle>;
  imageRendering$!: Observable<string | null>;
  crossOrigin$!: Observable<string | null | undefined>;
  store = inject(AbstractSensorContentStore2D);
  preserveAspectRatio!: string;

  constructor(
    private sanitizer: DomSanitizer,
    injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.sensorRendererState$ = this.store('sensorRendererState').$;
    this.src$ = this.buildSrc$();
    this.filter$ = this.buildFilter$();
    this.imageRendering$ = this.buildImageRendering$();
    this.crossOrigin$ = this.buildCrossOrigin$();
    this.preserveAspectRatio = isOrthographicVideoOutput(
      this.store.state().output,
    )
      ? 'xMinYMin meet'
      : 'xMidYMid meet';
  }

  private buildSrc$() {
    return combineLatest([
      this.store.currentSensorFrame$,
      this.store('layerSettings')('altImageEnabled').$,
    ]).pipe(
      map(([sensorFrame, altImageEnabled]) => {
        if (!isDefined(sensorFrame)) {
          return undefined;
        }

        if (
          altImageEnabled &&
          this.isInsideZoomPanel &&
          sensorFrame.altImageUrl
        ) {
          return sensorFrame.altImageUrl;
        }

        return sensorFrame.imageUrl;
      }),
    );
  }

  private buildFilter$() {
    return this.store('layerSettings').$.pipe(
      filter(isDefined),
      map((layerSettingsState) => {
        let brightness = layerSettingsState.brightness / 50;
        if (brightness > 1) {
          brightness = 20 ** (brightness - 1);
        }

        let contrast = layerSettingsState.contrast / 50;
        if (contrast > 1) {
          contrast = 30 ** (contrast - 1);
        }

        return this.sanitizer.bypassSecurityTrustStyle(
          `brightness(${brightness}) contrast(${contrast})`,
        );
      }),
    );
  }

  private buildImageRendering$() {
    return this.store('output')('meta')('use_anti_aliasing').$.pipe(
      map((useAntiAliasing) => (useAntiAliasing ? null : 'pixelated')),
    );
  }

  private buildCrossOrigin$() {
    return this.store.currentSensorFrame$.pipe(
      map((sensorFrame) => sensorFrame?.crossOrigin),
    );
  }
}
